import { Injectable, signal } from '@angular/core';
import { SCROLL_OFFSET } from '../config/constans';

@Injectable({
  providedIn: 'root'
})
export class ScrollService {
  // State signals
  private _isScrolled = signal(false);
  public isScrolled = this._isScrolled.asReadonly()

  // Local variables
  private _scrollTarget: HTMLElement | null = null;
  private _scrollTimeout: ReturnType<typeof setTimeout> | null = null;
  
  // Setters
  public setIsScrolled(value: boolean) {
    this._isScrolled.set(value);
  }

  // Methods
  /**
   *  @description Handles the scroll event by setting isScrolled to true and 
   * resetting it to false after a delay
   */
  private _onScroll = () => {
    this._isScrolled.set(true);

    if (this._scrollTimeout) clearTimeout(this._scrollTimeout);

    this._scrollTimeout = setTimeout(() => {
      this._isScrolled.set(false);
    }, 300);
  };

  /**
   * @description Attaches a scroll listener to the given element
   * @param el 
   * @returns 
   */
  attachScrollListener(el: HTMLElement): void {
    if (!el) return;
    this._scrollTarget = el;
    el.addEventListener('scroll', this._onScroll, { passive: true });
  }

  /**
   * @description Detaches the scroll listener from the previously attached element
   */
  detachScrollListener(): void {
    if (this._scrollTarget) {
      this._scrollTarget.removeEventListener('scroll', this._onScroll);
      this._scrollTarget = null;
    }

    if (this._scrollTimeout) {
      clearTimeout(this._scrollTimeout);
      this._scrollTimeout = null;
    }
  }
  
  /**
   * @description Scrolls the given element to the top
   * @param el The element to scroll
   * @returns 
   */
  autoScrollToTop = (el: HTMLElement) => {
    if (!el) return;

    setTimeout(() => {
      el.scrollTo({ top: 0, behavior: 'smooth' });
    }, 0);
  }

  /**
   * @description Scrolls to the element with the specified frame index within the container
   * @param container The container element
   * @param trameIndex The frame index to scroll to
   * @returns 
   */
  scrollToElementByTrameIndex = ( container: HTMLElement, trameIndex: number = 0 ): void => {
    const target = container?.querySelector(`[data-header-point-scroll-index="${trameIndex}"]`);
    if (!target) {
      console.warn(`Element with data-header-point-scroll-index="${trameIndex}" not found.`);
      return;
    }

    const targetElement = target as HTMLElement;
    const targetTop = this.getOffsetTopRelativeToContainer(targetElement, container);
    container.scrollTo({
      top: targetTop >= (SCROLL_OFFSET * 2) 
        ? targetTop - SCROLL_OFFSET 
        : 0,
      behavior: 'smooth'
    });
  }

  /**
   * @description Gets the offset top position of an element relative to a container
   * @param element The element to measure
   * @param container The container element
   * @returns The offset top position in pixels
   */
  private getOffsetTopRelativeToContainer = (element: HTMLElement, container: HTMLElement): number => {
    let offset = 0;
    let current: HTMLElement | null = element;
    while (current && current !== container) {
      offset += current.offsetTop;
      current = current.offsetParent as HTMLElement;
    }
    return offset;
  }
  
  /**
   * @description Scrolls to the element with the specified hour within the container
   * @param container The container element
   * @param hour The hour to scroll to
   * @returns
   */
  scrollToElementByTimelineHour = ( container: HTMLElement, hour: number = 0 ): void => {
    const target = container.querySelector(`[data-hour="${hour}"]`);
    if (!target) {
      console.warn(`Element with data-header-point-scroll-index="${hour}" not found.`);
      return;
    }

    const targetElement = target as HTMLElement;
    const targetTop = this.getOffsetTopRelativeToContainer(targetElement, container);
    container.scrollTo({
      top: targetTop >= (SCROLL_OFFSET * 2)
        ? targetTop - SCROLL_OFFSET
        : 0,
      behavior: 'smooth'
    });
  }
}
